import { lazy } from 'react';

import { useAppContext } from '@app/app-context';
import { useAppSelector } from '@store/hooks';
import Page from '@components/page';
import { usePermissions } from '@components/permissions';
import useIsAuthenticated from '@hooks/use-is-authenticated';

import { useRouteContext } from './router.hooks';

const Login = lazy(() => import('@features/login'));

/**
 * Renders the current route component. Gathers various contexts and passes them
 * down as props. The component is replaced with the Login screen if the user is
 * not authenticated.
 */
export const RouteContainer = () => {
  // These hooks return a Deferred state which provides a buffer for priority
  // updates to settle before the route is re-rendered, preventing error flashes
  // during a transition.
  const routeContext = useRouteContext();
  const permissionsContext = usePermissions();
  const isAuthenticated = useIsAuthenticated();

  const { serviceCycler, ...appData } = useAppContext();

  const { isEngineHealthy, isLicenseGood } = useAppSelector(
    state => state.app.healthCheck,
  );

  const { Component, ...context } = routeContext;

  // Logout is handled immediately, replacing the route component with the Login
  // screen. Deferred auth state provides a post-auth buffer to avoid the route
  // component being rendered with stale context, leaving Login component in
  // place for an extra cycle while the context settles.
  if (!isAuthenticated) {
    return (
      <Page skeleton>
        <Login
          actions={context.route.actions}
          isLdapEnabled={appData.isLdapEnabled}
          isSsoAuthOnly={appData.isSsoAuthOnly}
        />
      </Page>
    );
  }

  return (
    <Component
      // TODO: Refactor consumers to avoid passing so much context as props
      {...appData}
      {...context}
      {...permissionsContext}
      isAuthenticated={isAuthenticated}
      isEngineHealthy={isEngineHealthy}
      isLicenseGood={isLicenseGood}
      serviceCycler={serviceCycler}
    />
  );
};

export default RouteContainer;
